import { ChangeDetectorRef, Component } from '@angular/core';
import { HeaderComponent } from '../../../../layout/header/header.component';
import { NavbarComponent } from '../../../../layout/navbar/navbar.component';
import { SidebarComponent } from '../../../../layout/sidebar/sidebar.component';
import { RoutelinksService } from '../../../../services/core/routelinks.service';
import { UserService } from '../../../../services/user.service';
import { User } from '../../../../models/user';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Role } from '../../../../models/role';
import { UserRequest } from '../../../../models/userRequest';
import { LangChangeEvent, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserResponse } from '../../../../models/userResponse';
import { SharedService } from '../../../../services/shared.service';
import { PaginatorComponent } from "../../../../components/paginator/paginator.component";
import { Subscription } from 'rxjs';
import { AlertComponentComponent } from "../../../../components/alert/alert-component.component";
import { messageVisibility } from '../../../../models/messageVisibility.model';
import { pageDto } from '../../../../models/pagedto';
import { MessageInputComponent } from "../../../../layout/message-input/message-input.component";

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [HeaderComponent, NavbarComponent, SidebarComponent, CommonModule, ReactiveFormsModule, TranslatePipe, PaginatorComponent, AlertComponentComponent, MessageInputComponent],
  templateUrl: './users.component.html',
  styleUrl: './users.component.css'
})
export class UsersComponent {
  links: any[] = []; // Lista de links para el sidebar

  userReponse: UserResponse = new UserResponse();

  listRol: string[] = [];

  selectedRowId: number | null = null;

  userRequest = new UserRequest();

  user = new User();

  creatingUser: boolean = false;

  updateUser: boolean = false;

  loadListing: boolean = true;

  validateForm!: FormGroup;

  langChangeSubscription!: Subscription;

  messageVisibility: messageVisibility = new messageVisibility()

  filtering: boolean = false;

  filteredUsers: User[] = [];

  errorMessage: string = '';

  constructor(private routelinks: RoutelinksService,
    private userService: UserService,
    private changeDetector: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    public sharedService: SharedService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.links = this.routelinks.linksAdministration; // Inicializa la propiedad en ngOnInit
    this.getAllUser(true);
    this.getRolMaster();
    this.selectedRowId = null;
    this.initFormReactiveUser();

    this.sharedService.numberPageEventEmitter.subscribe((page: number) => {
      this.userReponse.pageDto.currentPage = page;
      this.getAllUser(false);
    });

    this.langChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.changeDetector.detectChanges();
      this.getAllUser(true);
    });
  }

  /**
   * Función encargada e listar todos los usuarios autorizados dentro de la aplicación
   * @param init 
   */
  getAllUser(init: boolean): void {
    if (!init) {
      this.userRequest.pageRequest.page = this.userReponse.pageDto.currentPage;
      if (this.filtering) {
        this.handleFindNameOrIdp({ target: { value: this.filtering } });
      }
    }
    if (!this.filtering) {
      this.userService.getListUser(this.userRequest.pageRequest).subscribe({
        next: response => {
          this.userReponse.listUser = response.tariffAgentDto;
          if (response.pageDto !== undefined) {
            this.userReponse.pageDto = response.pageDto;
          }
        }, error: error => {
          this.loadListing = false;
          console.log('Se ha producido un error', error);
        }
      });
    }
  }

  isAdminActive(user: User): string {
    return user.agentFunctionsDto?.find(rol => rol.functionCode === 'ROLE_ADMIN')?.active ? 'Administrador' : 'Gestor';
  }

  /**
   * Función que definie el formulario reactivo
   */
  private initFormReactiveUser(): void {
    this.validateForm = this.formBuilder.group({
      idTariffAgent: ['', [Validators.required, this.decimalValidator]],
      functionCode: ['', [Validators.required]],
      active: new FormControl(true),
      name: ['', [Validators.required]],
      surname: ['', [Validators.required]]
    });
  }

  /**
   * Funcíon encargada de crear o actualizar a los usuarios autorizados dentro de la aplicación
   */
  createOrUpdateUser(): void {
    this.validateForm.markAllAsTouched()
    this.userRequest.user = this.validateForm.value;
    this.userRequest.user.agentFunctionsDto = Array.isArray(this.validateForm.value.functionCode)
      ? this.validateForm.value.functionCode.map((code: string) => ({
        functionCode: code
      })) : [];
    if (!this.updateUser) {
      this.userService.createUserAuthoriation(this.userRequest).subscribe({
        next: () => {
          this.messageVisibility.isErrorFirts = false;
          this.messageVisibility.isSuccessFirst = true;
          this.errorMessage = "S'ha creat correctament"
          this.resetValues();
          this.creatingUser = false;
        }, error: error => {
          this.messageVisibility.isErrorFirts = true;
          this.messageVisibility.isSuccessFirst = false;
          this.errorMessage = error.error.message
          console.log(error);
        }
      });
    } else {
      this.userService.updateUserAuthoriation(this.userRequest, true).subscribe({
        next: () => {
          this.messageVisibility.isErrorFirts = false;
          this.messageVisibility.isSuccessFirst = true;
          this.errorMessage = "S'ha editat correctament"
        }, error: error => {
          this.messageVisibility.isErrorFirts = true;
          this.messageVisibility.isSuccessFirst = false;
          this.errorMessage = error.error.message
          console.log(error);
        }
      });
    }
  }

  /**
   * Función encargadad de listar los rol maestros de la aplicación
   */
  getRolMaster(): void {
    this.userService.getListRolesMaster().subscribe({
      next: (response: Role[]) => {
        if (response != null) {
          response.forEach(rol => {
            if (rol.functionCode !== undefined) {
              this.listRol.push(rol.functionCode ?? '');
            }
          });
        }
      }, error: error => {
        console.log(error)
      }
    });
  }

  /**
   * Función encargada de realizar el modo edición o creación del usuario
   * @param user 
   */
  checkRow(user?: User): void {
    if (user) {
      if (this.selectedRowId === user.idTariffAgent) {
        this.selectedRowId = null;
        return;
      }
      // Modo edición
      this.selectedRowId = Number(user.idTariffAgent);
      this.updateUser = true;
      this.creatingUser = false;
      this.validateForm.patchValue({
        idTariffAgent: user.idTariffAgent,
        active: user.active,
        functionCode: user.agentFunctionsDto.map(rol => rol.functionCode).filter((code): code is string => code !== undefined),
        name: user.name,
        surname: user.surname
      });

    } else {
      // Modo creación
      this.selectedRowId = null;
      this.updateUser = false;
      this.creatingUser = true;
      this.validateForm.reset();
      this.validateForm.patchValue({
        active: true
      });
    }
    window.scrollTo(0, 600);
    this.changeDetector.detectChanges();
  }

  /**
   * Función encargadad de elimnar al usuario autorizado dentro de la aplicación
   */
  deleteUserById(): void {
    this.userService.deleteUser(this.selectedRowId || 0).subscribe({
      next: () => {
        this.resetValues();
      }, error: error => {
        console.log(error);
      }
    });
  }

  private resetValues(): void {
    this.getAllUser(true);
    this.validateForm.reset();
    this.selectedRowId = null;
  }

  /**
     * Función encargada de validar 
     * @returns 
     */
  decimalValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const isValid = /^\d+(\.\d{1,2})?$/.test(value.toString());
      return isValid ? null : { invalidDecimal: true };
    };
  }

  /**
     * Función encargada de validar el formato para un input del tipo float
     * @param event 
     */
  validarDecimales(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.match(/^\d+(\.\d{0,2})?/)?.[0] || '';

  }

  /**
   * Función encargada de establecer los usuarios paginados al filtrar
   * @param users 
   */
  private setPaginatedUsers(users: User[]): void {
    this.userReponse.pageDto.start = (this.userReponse.pageDto.currentPage === 1);
    this.userReponse.pageDto.end = (this.userReponse.pageDto.currentPage === this.userReponse.pageDto.pages);
    const startIndex = (this.userReponse.pageDto.currentPage - 1) * this.userReponse.pageDto.pageSize;
    const endIndex = startIndex + this.userReponse.pageDto.pageSize;
    this.userReponse.listUser = users.slice(startIndex, endIndex);
  }

  /**
   * Función encargada de filtrar el listado por el IDP o el nombre del ususario
   * @param event 
   * @returns 
   */
  handleFindNameOrIdp(event: any) {
    if (event.target.value === "") {
      this.userRequest.pageRequest.page = 1;
      this.filtering = false;
      this.getAllUser(true);
      return;
    }
    if (!this.filtering) {
      this.userService.findByNameOrIdp(event.target.value).subscribe({
        next: response => {
          if (response.length >= 1) {
            this.filteredUsers = response;

            // Indicamos que se está filtrando
            this.filtering = true;

            // Establecemos los nuevos valores de paginación
            this.userReponse.pageDto.currentPage = 1;
            this.userReponse.pageDto.pages = Math.ceil(response.length / this.userReponse.pageDto.pageSize);
            this.userReponse.pageDto.records = response.length;

            // Dividimos la lista de usuarios en función del tamaño de página
            this.userReponse.listUser = response.slice(0, this.userReponse.pageDto.pageSize);

            this.setPaginatedUsers(response);
          } else {
            this.getAllUser(true);
          }
        }, error: error => {
          console.log(error);
        }
      })
    } else {
      // Devolvemos el slice actual dependiendo de la current page
      this.setPaginatedUsers(this.filteredUsers);
    }
  }
}